import { useState } from 'react'

import { Button, TextField, makeStyles } from '@material-ui/core'
import { Field, Form, Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { LOGIN_ERRORS, SignInFormValues } from './SignInForm.types'

const useStyles = makeStyles((theme) => ({
  credentialsForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
}))

type CredentialsFormProps = {
  onSubmit: (values: SignInFormValues) => Promise<void>
  onError: (error: string) => void
}

const RETRY_HINT = 'Change your email or password to try again.'

const sameCredentials = (a: SignInFormValues | null, b: SignInFormValues) =>
  a?.email === b.email && a?.password === b.password

const yupValidation = Yup.object().shape({
  email: Yup.string()
    .required('Please enter a valid email.')
    .email('Please enter a valid email.'),
  password: Yup.string().required('Please enter your password.'),
})

const CredentialsForm = ({ onSubmit, onError }: CredentialsFormProps) => {
  const classes = useStyles()
  const [rejected, setRejected] = useState<SignInFormValues | null>(null)

  const handleSubmit = async (
    values: SignInFormValues,
    formikHelpers: FormikHelpers<SignInFormValues>
  ) => {
    if (sameCredentials(rejected, values)) {
      return
    }
    formikHelpers.setSubmitting(true)
    try {
      await onSubmit(values)
    } catch (err: any) {
      formikHelpers.setSubmitting(false)

      // Check if we have a status code that maps to a known error
      const statusCode = err?.status || err?.statusCode
      if (statusCode === 403) {
        setRejected(values)
      }
      if (statusCode && LOGIN_ERRORS[statusCode as keyof typeof LOGIN_ERRORS]) {
        onError(LOGIN_ERRORS[statusCode as keyof typeof LOGIN_ERRORS])
      } else if (err?.text) {
        const text = await err.text()
        onError(text)
      } else if (err?.data) {
        onError(err.data)
      } else {
        onError('An error occurred while signing in')
      }
    }
  }

  const initialValues: SignInFormValues = {
    email: '',
    password: '',
  }

  return (
    <Formik
      initialValues={initialValues}
      validateOnMount
      validateOnChange={false}
      validateOnBlur={true}
      validationSchema={yupValidation}
      onSubmit={handleSubmit}
    >
      {({ errors, touched, isValid, isSubmitting, values, setFieldError, handleChange }) => {
        const handleFieldChange = (fieldName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
          // Clear error when user starts typing
          if (errors[fieldName as keyof SignInFormValues]) {
            setFieldError(fieldName, undefined)
          }
          handleChange(e)
        }
        const blocked = sameCredentials(rejected, values)

        return (
          <Form className={classes.credentialsForm}>
            <Field
              id="email"
              name="email"
              as={TextField}
              required
              autoFocus
              fullWidth
              variant="outlined"
              label="Email"
              error={blocked || (errors.email && touched.email && values.email !== '')}
              helperText={errors.email && touched.email && values.email !== '' ? errors.email : ''}
              onChange={handleFieldChange('email')}
            />
            <Field
              id="password"
              type="password"
              name="password"
              as={TextField}
              required
              fullWidth
              variant="outlined"
              label="Password"
              error={blocked || (errors.password && touched.password && values.password !== '')}
              helperText={
                errors.password && touched.password && values.password !== ''
                  ? errors.password
                  : blocked
                    ? RETRY_HINT
                    : ''
              }
              onChange={handleFieldChange('password')}
            />
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={!isValid || isSubmitting || blocked}
            >
              Log In
            </Button>
          </Form>
        )
      }}
    </Formik>
  )
}

export default CredentialsForm
